// Semantic structure (schema v2). Blocks attach menu meaning and layout
// policy to the existing layer-group tree: a block's key IS a group id from
// node.groupPath, so membership has a single source of truth and cannot
// drift from what the Layers panel shows.
//
// doc.blocks[groupId] = {
//   kind: "section" | "heading" | "item" | "group",
//   name, pageId, cmsSectionId?, cmsItemId?,
//   placement: { mode: "fixed" | "flow" },       // fixed = never moved by a parent layout
//   size:      { mode: "fixed" | "auto" },
//   flow:      { mode: "reflow" | "preserve-breaks" },
//   layout:    { mode: "free" | "stack" | "row" | "grid", gap, columns, columnGap,
//                align: "start" | "center", origin: {x, y}, width, order: [childKey],
//                spaceBefore: { childKey: pt } },
//   locked?: true,
// }
// A child key is "g:<groupId>" for a child group or "n:<nodeId>" for a node
// that belongs directly to the container.
// Every inferred block starts with a free layout, so migration never moves anything.
// pageId is informational; layout always follows actual group membership.

const { resolveStyle } = require("./styles");
const { resolveText, textRuns } = require("./text");

const LAYOUT_MODES = ["free", "stack", "row", "grid"];
const KINDS = ["section", "heading", "item", "group"];
const round = (n) => Math.round(n * 10000) / 10000;
const groupPath = (node) => (Array.isArray(node.groupPath) ? node.groupPath : node.groupId ? [{ id: node.groupId, name: "Group" }] : []);

function boundsOf(nodes) {
  if (!nodes.length) return null;
  const x = Math.min(...nodes.map((n) => n.x)), y = Math.min(...nodes.map((n) => n.y));
  return { x, y, w: Math.max(...nodes.map((n) => n.x + n.w)) - x, h: Math.max(...nodes.map((n) => n.y + n.h)) - y };
}

// Group tree of one page: Map groupId → { id, name, parent, nodes: [direct], children: [groupIds] }
function groupTree(page) {
  const groups = new Map();
  const roots = { children: [], nodes: [] };
  for (const node of page.elements) {
    const path = groupPath(node);
    let parent = null;
    path.forEach((g) => {
      if (!groups.has(g.id)) {
        groups.set(g.id, { id: g.id, name: g.name, parent, nodes: [], children: [] });
        (parent ? groups.get(parent).children : roots.children).push(g.id);
      }
      parent = g.id;
    });
    (parent ? groups.get(parent).nodes : roots.nodes).push(node);
  }
  return { groups, roots };
}

function subtreeNodes(tree, id, out = []) {
  const g = tree.groups.get(id);
  if (!g) return out;
  out.push(...g.nodes);
  g.children.forEach((child) => subtreeNodes(tree, child, out));
  return out;
}

const bindingId = (node, type, field) => {
  const m = /^(item|section):([^:]+):(.+)$/.exec(node.binding || "");
  return m && m[1] === type && (!field || m[3] === field) ? m[2] : null;
};

// Infer blocks from the current groups. Existing block records are kept as-is.
function inferBlocks(doc) {
  const blocks = { ...(doc.blocks || {}) };
  for (const page of doc.pages) {
    const tree = groupTree(page);
    for (const g of tree.groups.values()) {
      if (blocks[g.id]) continue;
      const direct = g.nodes, all = subtreeNodes(tree, g.id);
      const itemId = direct.map((n) => bindingId(n, "item", "name")).find(Boolean);
      const headerId = direct.map((n) => bindingId(n, "section", "header")).find(Boolean);
      let kind = "group", extra = {};
      if (itemId) { kind = "item"; extra = { cmsItemId: itemId }; }
      else if (headerId && / · heading$/.test(g.name || "")) { kind = "heading"; extra = { cmsSectionId: headerId }; }
      else {
        const sectionId = all.map((n) => bindingId(n, "section", "header")).find(Boolean) ||
          (g.parent === null && all.find((n) => n.sectionId) ? all.find((n) => n.sectionId).sectionId : null);
        if (sectionId && g.parent === null) { kind = "section"; extra = { cmsSectionId: sectionId }; }
      }
      blocks[g.id] = {
        kind, name: g.name, pageId: page.id, ...extra,
        // Top-level blocks hold their place on the page; nested blocks follow
        // their container once it is converted to a flowing layout.
        placement: { mode: g.parent === null ? "fixed" : "flow" }, size: { mode: "fixed" }, flow: { mode: "preserve-breaks" },
        layout: { mode: "free" },
      };
    }
  }
  return blocks;
}

function migrateToV2(doc) {
  return { ...doc, schema: 2, blocks: inferBlocks(doc) };
}

// Children of a container in layout order.
function containerChildren(tree, id, block) {
  const g = tree.groups.get(id);
  const children = [
    ...g.children.map((child) => ({ key: `g:${child}`, nodes: subtreeNodes(tree, child), groupId: child })),
    ...g.nodes.map((node) => ({ key: `n:${node.id}`, nodes: [node] })),
  ].filter((c) => c.nodes.length);
  children.forEach((c) => { c.box = boundsOf(c.nodes); });
  const order = (block.layout && block.layout.order) || [];
  const rank = new Map(order.map((key, i) => [key, i]));
  return children.sort((a, b) => {
    const ra = rank.has(a.key) ? rank.get(a.key) : Infinity, rb = rank.has(b.key) ? rank.get(b.key) : Infinity;
    if (ra !== rb) return ra - rb;
    return a.box.y - b.box.y || a.box.x - b.box.x;
  });
}

// Derive display positions for flowing containers. Pure: returns a new doc and
// never changes authored data. Fixed children keep their authored position.
function layoutDocument(doc) {
  if (!doc.blocks || !Object.keys(doc.blocks).length) return doc;
  let changed = false;
  const pages = doc.pages.map((page) => {
    const flowing = (id) => doc.blocks[id] && doc.blocks[id].layout && doc.blocks[id].layout.mode !== "free";
    if (!page.elements.some((n) => groupPath(n).some((g) => flowing(g.id)))) return page;
    const elements = page.elements.map((n) => ({ ...n }));
    const working = { ...page, elements };
    const tree = groupTree(working);
    const shift = (nodes, dx, dy) => {
      if (!dx && !dy) return;
      const ids = new Set(nodes.map((n) => n.id));
      // Attached artwork outside the group travels with its anchor.
      for (const n of elements) if (!ids.has(n.id) && n.attachedTo && ids.has(n.attachedTo)) ids.add(n.id);
      for (const n of elements) if (ids.has(n.id)) { n.x = round(n.x + dx); n.y = round(n.y + dy); }
      changed = true;
    };
    // Post-order: inner containers settle before their parents measure them.
    const visit = (id) => {
      const g = tree.groups.get(id);
      g.children.forEach(visit);
      const block = doc.blocks[id];
      if (!block || !block.layout || block.layout.mode === "free") return;
      const children = containerChildren(tree, id, block);
      children.forEach((c) => { c.box = boundsOf(c.nodes); });
      const flowing = children.filter((c) => !isPinned(doc, block, c));
      const { mode, gap = 0, columns = 2, columnGap = 0, align = "start" } = block.layout;
      const origin = block.layout.origin || (flowing[0] ? { x: flowing[0].box.x, y: flowing[0].box.y } : { x: 0, y: 0 });
      const width = block.layout.width || 0;
      const spaceBefore = block.layout.spaceBefore || {};
      if (mode === "stack") {
        let cursor = origin.y;
        flowing.forEach((c, i) => {
          const before = i === 0 ? (spaceBefore[c.key] || 0) : (spaceBefore[c.key] !== undefined ? spaceBefore[c.key] : gap);
          const top = cursor + before;
          const dx = align === "center" && width ? origin.x + width / 2 - (c.box.x + c.box.w / 2) : 0;
          shift(c.nodes, dx, top - c.box.y);
          cursor = top + c.box.h;
        });
      } else if (mode === "row") {
        let cursor = origin.x;
        flowing.forEach((c, i) => {
          const before = i === 0 ? (spaceBefore[c.key] || 0) : (spaceBefore[c.key] !== undefined ? spaceBefore[c.key] : gap);
          const left = cursor + before;
          shift(c.nodes, left - c.box.x, 0);
          cursor = left + c.box.w;
        });
      } else if (mode === "grid") {
        const cols = Math.max(1, Math.floor(columns));
        const colWidth = width ? (width - columnGap * (cols - 1)) / cols : Math.max(...flowing.map((c) => c.box.w), 0);
        let rowTop = origin.y;
        for (let r = 0; r * cols < flowing.length; r++) {
          const row = flowing.slice(r * cols, r * cols + cols);
          if (r > 0) rowTop += spaceBefore[row[0].key] !== undefined ? spaceBefore[row[0].key] : gap;
          for (let i = 0; i < row.length; i++) {
            const c = row[i], cellX = origin.x + i * (colWidth + columnGap);
            const left = align === "center" ? cellX + (colWidth - c.box.w) / 2 : cellX;
            shift(c.nodes, left - c.box.x, rowTop - c.box.y);
          }
          rowTop += Math.max(...row.map((c) => c.box.h));
        }
      }
    };
    tree.roots.children.forEach(visit);
    return working;
  });
  return changed ? { ...doc, pages } : doc;
}

// A pinned child keeps its authored position inside a flowing parent:
// child groups via their own placement, direct nodes via layout.pinned.
function isPinned(doc, parent, child) {
  if (!child.groupId) return !!(parent.layout && Array.isArray(parent.layout.pinned) && parent.layout.pinned.includes(child.key));
  const block = doc.blocks[child.groupId];
  return !!(block && block.placement && block.placement.mode === "fixed");
}

// Convert a free container to a flowing layout that reproduces the current
// positions: measured gaps become spaceBefore overrides where they differ
// from the dominant gap. Returns the updated block record.
function convertBlock(doc, blockId, mode, options = {}) {
  if (!LAYOUT_MODES.includes(mode)) throw new Error(`Unknown layout mode ${mode}.`);
  const block = (doc.blocks || {})[blockId];
  if (!block) throw new Error(`No block ${blockId}.`);
  const page = doc.pages.find((p) => p.elements.some((n) => groupPath(n).some((g) => g.id === blockId)));
  if (!page) throw new Error("The block's group no longer exists.");
  const tree = groupTree(page);
  if (mode === "free") return { ...block, layout: { mode: "free" } };
  const children = containerChildren(tree, blockId, { layout: {} }).filter((c) => !isPinned(doc, block, c));
  if (!children.length) throw new Error("This block has nothing to lay out.");
  const box = boundsOf(children.flatMap((c) => c.nodes));
  const layout = { mode, order: children.map((c) => c.key), origin: { x: round(children[0].box.x), y: round(children[0].box.y) }, width: round(box.w) };
  const gaps = [];
  if (mode === "stack") children.forEach((c, i) => { if (i) gaps.push(round(c.box.y - (children[i - 1].box.y + children[i - 1].box.h))); });
  if (mode === "row") children.forEach((c, i) => { if (i) gaps.push(round(c.box.x - (children[i - 1].box.x + children[i - 1].box.w))); });
  if (mode === "grid") {
    const cols = Math.max(1, Math.floor(options.columns || 2));
    layout.columns = cols;
    layout.columnGap = Number.isFinite(options.columnGap) ? options.columnGap : 0;
    layout.align = options.align || "start";
    layout.origin = { x: round(box.x), y: round(children[0].box.y) };
    for (let r = 1; r * cols < children.length; r++) {
      const prev = children.slice((r - 1) * cols, r * cols), row = children[r * cols];
      gaps.push(round(row.box.y - Math.max(...prev.map((c) => c.box.y)) - Math.max(...prev.map((c) => c.box.h))));
    }
  }
  if (mode === "stack" && options.align === "center") layout.align = "center";
  const dominant = Number.isFinite(options.gap) ? options.gap : mostCommon(gaps);
  layout.gap = dominant === undefined ? 0 : dominant;
  if (options.gap === undefined) {
    const spaceBefore = {};
    if (mode === "grid") {
      const cols = layout.columns;
      gaps.forEach((g, i) => { if (g !== layout.gap) spaceBefore[children[(i + 1) * cols].key] = g; });
    } else gaps.forEach((g, i) => { if (g !== layout.gap) spaceBefore[children[i + 1].key] = g; });
    if (Object.keys(spaceBefore).length) layout.spaceBefore = spaceBefore;
  }
  if (block.layout && block.layout.pinned) layout.pinned = block.layout.pinned;
  return { ...block, layout };
}

// The most frequent exact gap (ties: the smaller). Exact values matter: any
// child whose gap differs at all gets its own spaceBefore, so conversion
// reproduces current positions to the last decimal.
function mostCommon(values) {
  if (!values.length) return undefined;
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
}

// Everything that determines how the page prints, per node: frame, resolved
// style, resolved text and the drawn runs. Used by baselines and gates.
function layoutSignature(doc, content = {}) {
  const display = layoutDocument(doc);
  const out = {};
  for (const page of display.pages)
    for (const node of page.elements) {
      const entry = { page: page.id, type: node.type, x: round(node.x), y: round(node.y), w: round(node.w), h: round(node.h), rotation: node.rotation || 0, hidden: !!node.hidden, opacity: node.opacity };
      if (node.type === "text") {
        const style = resolveStyle(doc, node);
        const text = String(resolveText(node, content) ?? "");
        entry.style = style;
        entry.text = text;
        const { runs, original } = textRuns(node, style, text);
        entry.original = original;
        entry.runs = runs.map((r) => ({ text: r.text, x: round(r.x), y: round(r.y) }));
      } else if (node.type === "image") entry.src = node.assetId || (typeof node.src === "string" ? node.src.slice(0, 80) + node.src.length : "");
      out[node.id] = entry;
    }
  return out;
}

// Compare two signatures; returns differences grouped per node.
function compareSignatures(before, after, { tolerance = 0.01, ignoreStyle = false } = {}) {
  const differences = [];
  for (const id of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const a = before[id], b = after[id];
    if (!a || !b) { differences.push({ id, change: a ? "removed" : "added" }); continue; }
    const fields = [];
    for (const k of ["page", "type", "hidden", "opacity", "rotation", "text", "src", "original"]) if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) fields.push(k);
    for (const k of ["x", "y", "w", "h"]) if (Math.abs(a[k] - b[k]) > tolerance) fields.push(k);
    if (!ignoreStyle && JSON.stringify(sortKeys(a.style)) !== JSON.stringify(sortKeys(b.style))) fields.push("style");
    if (a.runs || b.runs) {
      const ra = a.runs || [], rb = b.runs || [];
      if (ra.length !== rb.length || ra.some((r, i) => r.text !== rb[i].text)) fields.push("lines");
      else if (ra.some((r, i) => Math.abs(r.x - rb[i].x) > tolerance || Math.abs(r.y - rb[i].y) > tolerance)) fields.push("baselines");
    }
    if (fields.length) differences.push({ id, page: b.page, fields });
  }
  return differences;
}
const sortKeys = (o) => (o && typeof o === "object" ? Object.fromEntries(Object.keys(o).sort().map((k) => [k, o[k]])) : o);

function validBlocks(doc) {
  const blocks = doc.blocks;
  if (blocks === undefined) return true;
  if (!blocks || typeof blocks !== "object" || Array.isArray(blocks)) return false;
  const finite = (v) => Number.isFinite(v) && Math.abs(v) < 100000;
  return Object.entries(blocks).length <= 5000 && Object.entries(blocks).every(([id, b]) =>
    typeof id === "string" && id.length <= 200 && b && typeof b === "object" &&
    KINDS.includes(b.kind) && (b.pageId === undefined || typeof b.pageId === "string") &&
    (!b.placement || ["fixed", "flow"].includes(b.placement.mode)) &&
    (!b.size || ["fixed", "auto"].includes(b.size.mode)) &&
    (!b.flow || ["reflow", "preserve-breaks"].includes(b.flow.mode)) &&
    (!b.layout || (LAYOUT_MODES.includes(b.layout.mode) &&
      ["gap", "columnGap", "width"].every((k) => b.layout[k] === undefined || finite(b.layout[k])) &&
      (b.layout.columns === undefined || (Number.isInteger(b.layout.columns) && b.layout.columns >= 1 && b.layout.columns <= 12)) &&
      (b.layout.origin === undefined || (finite(b.layout.origin.x) && finite(b.layout.origin.y))) &&
      (b.layout.pinned === undefined || (Array.isArray(b.layout.pinned) && b.layout.pinned.every((k) => typeof k === "string"))) &&
      (b.layout.order === undefined || (Array.isArray(b.layout.order) && b.layout.order.every((k) => typeof k === "string"))) &&
      (b.layout.spaceBefore === undefined || Object.values(b.layout.spaceBefore).every(finite)))));
}

// Text nodes whose preserved line breaks no longer fit their frame: the
// overflow warning that replaces silent truncation.
function overflowWarnings(doc, content = {}) {
  const warnings = [];
  for (const page of doc.pages)
    for (const node of page.elements) {
      if (node.type !== "text" || node.hidden) continue;
      const style = resolveStyle(doc, node);
      const { runs } = textRuns(node, style, String(resolveText(node, content) ?? ""));
      if (!runs.length) continue;
      const bottom = runs[runs.length - 1].y + style.fontSize * 0.3;
      if (bottom > node.h + Math.max(2, style.fontSize * 0.35)) warnings.push({ pageId: page.id, nodeId: node.id, lines: runs.length, overflow: round(bottom - node.h) });
    }
  return warnings;
}

module.exports = { inferBlocks, migrateToV2, layoutDocument, convertBlock, layoutSignature, compareSignatures, validBlocks, groupTree, subtreeNodes, boundsOf, overflowWarnings, LAYOUT_MODES, containerChildren };
